import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CameraInitResult, CameraService } from 'services/camera.service';
import { CameraStrategy, OverlayState, RectCssPx, WebCameraStrategy } from 'services/camera-strategy';
import { SettingsService } from 'services/settings.service';
import { LoggingService } from 'services/logging.service';
import { hasClientSmallScreen, isCapacitorNative, isPlatformIOS } from 'environments/environment-helpers';
import { PopoverController } from 'providers/popover-controller';
import { CsSelectPopover, CsSelectPopoverOption } from 'components/cs-select-popover/cs-select-popover';
import { BackdropMode } from 'components/popover-host/popover-host';
import { sleep, timeout } from '@cf-shared/shared-util';
import { describeError } from '@cf-shared/error-describe';
import { animationFrame, DestroyGuard } from 'services/browser-utils';
import { CsBarcodeDetectorDirective } from 'components/cs-barcode-detector/cs-barcode-detector.directive';
import { CsBarcodeTrackerOverlayComponent } from 'components/cs-barcode-tracker-overlay/cs-barcode-tracker-overlay';
import { TrackerPick, TrackerRect } from 'components/cs-barcode-tracker-overlay/barcode-tracker-selector';
import { transformDecoderRectToVideoSpace } from 'components/cs-barcode-tracker-overlay/barcode-tracker-coords';
import { BarcodeTrackerGate } from 'components/cs-barcode-tracker-overlay/barcode-tracker-gate';
import { QuaggaProcessResult } from 'services/barcode-decoder.service';

@Component({
  selector: 'cs-camera-view',
  templateUrl: 'cs-camera-view.html',
  host: { 'class': 'camera-view' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsCameraViewComponent implements OnInit, AfterViewInit, OnDestroy {

  // ── Inputs ──────────────────────────────────────────────────────────

  /** Aspect ratio for video constraints and capture cropping.
   *  '1:1' = square center-crop (label scanner), '4:3' = full-frame (meal scanner) */
  @Input() aspectRatio: '1:1' | '4:3' | 'auto' = 'auto';

  /** Max base64 string length before progressive compression */
  @Input() maxImageSize = 666_000;

  /** Ideal width/height for getUserMedia constraints */
  @Input() idealResolution = 1920;

  // ── Outputs ─────────────────────────────────────────────────────────

  /** Emitted after camera stream starts and capabilities (zoom/flash) are set up */
  @Output() cameraReady = new EventEmitter<void>();

  /** Emitted when camera initialization fails (NoCameraWorked) */
  @Output() cameraError = new EventEmitter<CameraInitResult>();

  /** Emitted when camera permission status changes */
  @Output() permissionChange = new EventEmitter<void>();

  // ── Public readonly properties ──────────────────────────────────────

  /** The underlying CameraService instance. Pages that need a single shared
   *  source of truth (e.g. barcode-scanner's permission-denied UI which must
   *  survive mount/unmount cycles) can pre-create and pass one in; otherwise
   *  the component constructs its own lazily in `ngOnInit`. Not `@Injectable()`
   *  — exposed for parents to read permission status, camera count, and
   *  flash/zoom state. */
  @Input() cameraService: CameraService;

  get isActive(): boolean { return this.cameraActive; }
  get isInitializing(): boolean { return this.cameraInitializing; }

  /** Strategy chosen at construction: native iOS plugin vs getUserMedia.
   *  Overridden per-instance when the barcode-detector directive is projected
   *  and the preferred strategy lacks barcode support (capability-aware
   *  fallback — see `ensureStrategyForContent`). */
  private _strategyOverride: CameraStrategy | null = null;
  private get strategy(): CameraStrategy {
    return this._strategyOverride ?? this.cameraService.strategy;
  }
  get isNative(): boolean { return this.strategy.isNative; }

  /** True when a `<cs-barcode-detector>` is projected. Drives template
   *  branching (skip the component-owned `<video>` elements — Quagga
   *  renders its own inside the container) and the barcode-init code path. */
  get isBarcodeMode(): boolean { return !!this.barcodeDetector; }

  // ── Internal state ──────────────────────────────────────────────────

  private activeCameraId: string = null;
  cameraActive = false;
  cameraInitializing = false;
  videoReady = false;
  activeStream: MediaStream = null;

  @ViewChild('videoElement') videoElementRef: ElementRef<HTMLVideoElement>;
  @ViewChild('videoElementBack') videoElementBackRef: ElementRef<HTMLVideoElement>;
  @ViewChild('cameraContainer') cameraContainerRef: ElementRef<HTMLElement>;
  @ViewChild('cameraFlipInner') cameraFlipInnerRef: ElementRef<HTMLElement>;

  /** Projected `<cs-barcode-detector>`. When present, `cs-camera-view`
   *  follows the barcode-init code path instead of the normal photo-capture
   *  one — see `design-docs/native-camera-preview.md`. */
  @ContentChild(CsBarcodeDetectorDirective) barcodeDetector?: CsBarcodeDetectorDirective;

  /** Bracket-tracker overlay rendered only in web barcode mode. Owns the
   *  visual idle-square + lock-on morph; `trackerGate` (below) holds the
   *  gate / selector / hold-timer state machine that drives it. */
  @ViewChild('trackerOverlay') trackerOverlay?: CsBarcodeTrackerOverlayComponent;

  cameraFlipped = false;
  cameraFlipping = false;

  private pinchStartDistance = 0;
  private pinchStartZoom = 0;
  private cachedZoomLabel: TranslatedString = untranslatedString('1x');

  // native-only state — preview frame tracking, overlay tap unsubscribe
  private nativeOverlayUnsub: (() => void) | null = null;
  private nativeFrameResizeObserver: ResizeObserver | null = null;
  private nativeFrameOrientationHandler: (() => void) | null = null;
  private nativeFramePageTransitionHandler: ((event: TransitionEvent) => void) | null = null;
  private nativeFramePageElement: HTMLElement | null = null;
  private nativeFrameRaf: number | null = null;

  private destroyGuard = new DestroyGuard();

  /** First-morph gate + hold timer driving the tracker overlay (web barcode
   *  mode only). Declared after `destroyGuard`, which it uses as its timer
   *  owner so its timers are cancelled on destroy. */
  private trackerGate = new BarcodeTrackerGate({
    getOverlay: () => this.trackerOverlay,
    emitDetected: result => this.barcodeDetector?.emitDetected(result),
    timers: this.destroyGuard,
  });

  private prevOnPermissionChanged: (() => void) | undefined;

  // ── Constructor ─────────────────────────────────────────────────────

  constructor(
    private settings: SettingsService,
    private logging: LoggingService,
    private popoverCtrl: PopoverController,
    private cd: ChangeDetectorRef,
  ) {}

  // ── Lifecycle ───────────────────────────────────────────────────────

  ngOnInit() {
    // use the caller-provided cameraService (barcode-scanner relies on this so
    // its permission-denied template branch survives view teardown); fall
    // back to an owned instance otherwise (meal/label scanners don't care)
    if (!this.cameraService) {
      this.cameraService = new CameraService(this.settings, this.logging);
    }
  }

  async ngAfterViewInit() {
    // if permission changes to 'granted' (e.g. user enables via settings), retry camera.
    // Save + restore any pre-existing handler (the barcode-scanner page installs its
    // own in ngOnInit so the handler survives view unmount/remount cycles — without
    // restore, a permission grant after denial would hit the stale closure below and
    // early-return on `destroyGuard.isDestroyed`, deadlocking the remount).
    this.prevOnPermissionChanged = this.cameraService.onPermissionChanged;
    this.cameraService.onPermissionChanged = () => {
      if (this.destroyGuard.isDestroyed || this.activeCameraId) return;
      this.cameraService.cameraPermissionDismissed = false;
      this.permissionChange.emit();
      void this.initCameraAsync();
    };

    this.cameraInitializing = true;
    this.cd.markForCheck();
    await this.initCameraAsync();
    if (this.destroyGuard.isDestroyed) return;
    this.cameraInitializing = false;
    this.cd.markForCheck();
  }

  ngOnDestroy() {
    // restore the caller's handler (or clear ours) so a page-owned CameraService
    // retains whatever permission-change wiring it set up before the view mounted
    this.cameraService.onPermissionChanged = this.prevOnPermissionChanged;
    // stopCamera → stopStream already calls teardownNativeFrameTracking on
    // the native branch; no extra call needed. Fire-and-forget — Angular's
    // ngOnDestroy is sync; pages that want the native preview teardown to
    // complete before navigating should `await cameraView.stopCamera()`
    // themselves before the nav call.
    void this.stopCamera();
    this.destroyGuard.destroy();
  }

  // ── Public methods ──────────────────────────────────────────────────

  /** Capture the current video frame with aspect-ratio-based cropping and compression.
   *  Does NOT stop the camera — call stopCamera() separately if needed. */
  async capturePhoto(): Promise<{ dataUrl: string; base64: string }> {
    // '1:1' = square center-crop, otherwise full frame; native ignores the video element
    return this.strategy.capture({ aspectRatio: this.aspectRatio, maxImageSize: this.maxImageSize, video: this.activeVideo });
  }

  /** Stop current stream and re-initialize camera */
  async reinitCamera(forceCameraId?: string): Promise<void> {
    this.cameraInitializing = true;
    if (!this.destroyGuard.isDestroyed) this.cd.markForCheck();
    await this.initCameraAsync(forceCameraId);
    if (this.destroyGuard.isDestroyed) return;
    this.cameraInitializing = false;
    this.cd.markForCheck();
  }

  /** Stop camera stream and reset state. Returns a promise that resolves
   *  once the underlying teardown (including the native AVCaptureSession
   *  + preview-layer removal on iOS, or Quagga stop on web) has completed.
   *  Pages that navigate away right after stopping should `await` this so
   *  the preview doesn't linger on screen during the route transition —
   *  the native preview layer lives behind the WebView and only comes down
   *  once the plugin's async stop call round-trips to Swift. */
  async stopCamera(): Promise<void> {
    await this.stopStream();
    if (!this.destroyGuard.isDestroyed) this.cd.markForCheck();
  }

  /** Freeze the tracker-overlay on its current detected pose. Called by
   *  the barcode-scanner page synchronously from `onCodeDetected`, BEFORE
   *  its 500ms `sleep(500)` confirmation freeze, so the brackets stay on
   *  their yellow detected pose for the entire freeze instead of flashing
   *  back to the white-idle pose mid-freeze (the hold timer would
   *  otherwise fire ~100ms into the freeze). On native strategies this is
   *  a no-op — the native overlay has its own freeze contract. */
  freezeOverlay(): void {
    // No-op on native: the native overlay has its own freeze contract,
    // and the gate flags this method touches are only read by the web
    // path's per-frame handler.
    if (this.isNative) return;
    // Web path only: drop tracking + cancel the hold timer so the brackets
    // stay frozen on their current rendered pose.
    this.trackerGate.freeze();
  }

  /** Get the active video track from the web strategy: Quagga's track in
   *  barcode mode, the `getUserMedia` track in photo mode. Null on native. */
  getActiveTrack(): MediaStreamTrack | null {
    return this.strategy instanceof WebCameraStrategy ? this.strategy.getActiveTrack() : null;
  }

  /** Get the rendered `<video>` element. Null on native (no `<video>` is
   *  rendered) or before the camera-container is in the DOM. */
  getVideoElement(): HTMLVideoElement | null {
    return this.videoElementRef?.nativeElement ?? null;
  }

  /** Get the camera container DOM element (for parent animation positioning) */
  getCameraElement(): HTMLElement | null {
    return this.cameraContainerRef?.nativeElement ?? null;
  }

  // ── Camera initialization ──────────────────────────────────────────

  private get activeVideo(): HTMLVideoElement | undefined {
    return this.cameraFlipped
      ? this.videoElementBackRef?.nativeElement
      : this.videoElementRef?.nativeElement;
  }

  private async initCameraAsync(forceCameraId?: string) {
    // capability-aware strategy pick: if a barcode detector is projected and
    // the factory's preferred strategy doesn't support decoding, fall back to
    // WebCameraStrategy for this component instance (keeps Quagga running on
    // iOS native / Android native Phase A)
    this.ensureStrategyForContent();

    if (this.isBarcodeMode) {
      await this.initCameraBarcodeAsync(forceCameraId);
      return;
    }

    if (this.isNative) {
      await this.initCameraNativeAsync();
      return;
    }

    const { result, cameraId } = await this.cameraService.initCameraWithRotationAsync({
      openAsync: async (deviceId) => {
        await this.openCameraAsync(deviceId);
      },
      cleanupAfterFailure: async () => {
        await this.stopStream();
      },
    }, { forceCameraId });

    if (result === CameraInitResult.Success && cameraId) {
      this.activeCameraId = cameraId;
    }

    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();

    if (result === CameraInitResult.NoCameraWorked) {
      this.cameraError.emit(result);
    } else if (result === CameraInitResult.PermissionDenied) {
      this.permissionChange.emit();
    }
  }

  /** Pick a per-instance strategy based on what's projected into `<ng-content>`:
   *  when a `<cs-barcode-detector>` is present and the factory's preferred
   *  strategy can't decode, instantiate a `WebCameraStrategy` that can. Called
   *  at the top of every `initCameraAsync` so reinit paths honor the override. */
  private ensureStrategyForContent() {
    const preferred = this.cameraService.strategy;
    if (this.barcodeDetector && !preferred.supportsBarcodeDetection) {
      if (!(this._strategyOverride instanceof WebCameraStrategy)) {
        this._strategyOverride = new WebCameraStrategy(this.cameraService);
      }
    } else {
      this._strategyOverride = null;
    }
  }

  // ── Barcode init path (Quagga via WebCameraStrategy) ───────────────

  /** Barcode-mode init flow. Branches by strategy kind:
   *
   *   - **Native** (iOS `AVCaptureMetadataOutput`): the session must be
   *     started first via `strategy.start()` — `startBarcodeDetection`
   *     attaches the metadata output to an already-running session.
   *     Reuses `initCameraNativeAsync` for the start path, then layers
   *     detection on top.
   *
   *   - **Web** (Quagga via `WebCameraStrategy`): Quagga owns the
   *     `getUserMedia` call AND manages its own `<video>` element, so
   *     `strategy.start()` is skipped and `startBarcodeDetection` does
   *     the open + decode together, inside the rotation-fallback loop.
   */
  private async initCameraBarcodeAsync(forceCameraId?: string) {
    // Seed cameraInfos with Back/Front placeholders on Capacitor native so
    // the flip overlay pill renders before the decoder is live. We can only
    // do this on native because CameraService.listCamerasAsync collapses
    // to [environment, user] on Capacitor (passed as facingMode, not device-
    // Id). On PWA these strings aren't valid deviceIds — seeding them would
    // produce `getUserMedia({deviceId:{exact:'environment'}})` = Overcon-
    // strainedError. PWA instead waits for populateCamerasFromTrackAsync
    // to enumerate real devices after the first successful open.
    if (isCapacitorNative() && this.cameraService.cameraInfos.length < 2) {
      this.cameraService.cameraInfos = [
        { id: 'environment', name: 'Back', hasFlash: false, isSelfie: false },
        { id: 'user', name: 'Front', hasFlash: false, isSelfie: true },
      ];
    }

    if (this.isNative) {
      await this.initCameraBarcodeNativeAsync();
      return;
    }

    // Web path: camera-container must be in the DOM before Quagga renders into it
    this.cameraActive = true;
    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();
    await animationFrame();

    let container = this.cameraContainerRef?.nativeElement;
    if (!container) {
      for (let i = 0; i < 10 && !container; i++) {
        await sleep(100);
        if (this.destroyGuard.isDestroyed) return;
        container = this.cameraContainerRef?.nativeElement;
      }
    }
    if (!container) {
      this.cameraError.emit(CameraInitResult.NoCameraWorked);
      return;
    }

    const detector = this.barcodeDetector;
    const types = detector?.types ?? [];

    // This entire block is the web (Quagga) barcode-init path; the iOS
    // native path goes through `initCameraBarcodeNativeAsync` and never
    // reaches `runStart` below. With the overlay active on web, the
    // directive's `(detected)` emit is owned by the gated per-frame
    // selector pipeline (see handleProcessedForOverlay), so Quagga's
    // own onDetected callback is a no-op here — its noisy raw-detection
    // signal is replaced by the smoothed selector pick.
    this.startTrackerOverlay();
    const runStart = async (deviceId: string | null, options: { relaxedConstraints?: boolean } = {}) => {
      await this.strategy.startBarcodeDetection({
        target: container,
        deviceId,
        types,
        onDetected: () => { /* gated emit lives in handleProcessedForOverlay */ },
        onProcessed: r => {
          this.handleProcessedForOverlay(r);
          detector?.emitProcessed(r);
        },
        relaxedConstraints: options.relaxedConstraints,
        isDestroyed: () => this.destroyGuard.isDestroyed,
      });
    };

    const { result, cameraId } = await this.cameraService.initCameraWithRotationAsync({
      openAsync: async (deviceId) => {
        // timeout protects against stuck cameras (Galaxy Fold selfie, iOS multi-camera)
        const timeoutMs = forceCameraId ? 30_000 : isPlatformIOS() ? 15_000 : 5_000;
        if (deviceId) {
          await timeout(timeoutMs, runStart(deviceId));
        } else {
          // no timeout for facingMode — permission dialog has no time limit
          await runStart(null);
        }
      },
      cleanupAfterFailure: async () => {
        await this.strategy.stopBarcodeDetection(container);
      },
      retryOnOverconstrained: async (deviceId) => {
        try {
          await timeout(5_000, runStart(deviceId, { relaxedConstraints: true }));
          return true;
        } catch (err) {
          // the rotation loop moves on to the next camera, but this one could not be opened at all
          this.logging.trackSilentProblem('CameraRelaxedConstraintsRetryFailed', { error: describeError(err) });
          await this.strategy.stopBarcodeDetection(container);
          return false;
        }
      },
    }, { forceCameraId });

    if (this.destroyGuard.isDestroyed) return;

    if (result === CameraInitResult.Success) {
      if (cameraId) this.activeCameraId = cameraId;
      this.videoReady = true;
      this.cd.markForCheck();
      this.cameraReady.emit();
    } else if (result === CameraInitResult.NoCameraWorked) {
      this.cd.markForCheck();
      this.cameraError.emit(result);
    } else if (result === CameraInitResult.PermissionDenied) {
      this.cd.markForCheck();
      this.permissionChange.emit();
    }
  }

  /** Native barcode init: start the AVCaptureSession via the standard
   *  native init path (which emits `cameraReady`), then enable barcode
   *  detection on top. No rotation-loop — native strategy manages
   *  facingMode internally and the OS picks the best multi-lens camera. */
  private async initCameraBarcodeNativeAsync() {
    await this.initCameraNativeAsync();
    if (this.destroyGuard.isDestroyed) return;
    // initCameraNativeAsync bails silently on permission-denied / failure
    // by emitting cameraError or permissionChange — don't start detection
    // if the session isn't actually running.
    if (!this.cameraActive) return;

    const detector = this.barcodeDetector;
    if (!detector) return;

    try {
      await this.strategy.startBarcodeDetection({
        deviceId: null,
        types: detector.types,
        onDetected: r => detector.emitDetected(r),
        onProcessed: r => detector.emitProcessed(r),
        isDestroyed: () => this.destroyGuard.isDestroyed,
      });
    } catch (err) {
      this.logging.trackSilentProblem('NativeBarcodeDetectionStartFailed', { error: describeError(err) });
      // don't tear down the preview — user can still see the camera.
      // Falling back to web decoder is not feasible (native preview layer
      // can't be read into canvas); surface as a camera error instead.
      this.cameraError.emit(CameraInitResult.NoCameraWorked);
    }
  }

  // ── Native init path (AVFoundation plugin) ─────────────────────────

  private async initCameraNativeAsync() {
    // no css/body transparency needed — the Swift plugin masks the WKWebView
    // layer with a hole at the camera rect so the AVCapturePreviewLayer
    // (inserted below the WebView) shows through. the DOM stays fully
    // opaque; nav transitions, modals, and sibling pages paint normally.

    let status: 'granted' | 'denied' | 'prompt';
    try {
      status = await this.strategy.getPermissionStatus();
      if (status === 'prompt') {
        status = await this.strategy.requestPermission();
      }
    } catch (err) {
      // plugin not available / not registered — surface as a camera error
      // so the spinner clears instead of hanging forever
      this.logging.trackSilentProblem('NativeCameraPermissionCheckFailed', { error: describeError(err) });
      this.cameraActive = false;
      if (!this.destroyGuard.isDestroyed) {
        this.cd.markForCheck();
        this.cameraError.emit(CameraInitResult.NoCameraWorked);
      }
      return;
    }
    if (status !== 'granted') {
      this.cameraService.cameraPermissionDismissed = true;
      if (!this.destroyGuard.isDestroyed) {
        this.cd.markForCheck();
        this.permissionChange.emit();
      }
      return;
    }

    // make the container visible so getBoundingClientRect returns real coords
    this.cameraActive = true;
    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();
    await animationFrame();

    // wait for the page's slide-in animation to finish before measuring —
    // mid-slide the cs-page has a transform: translateX(...) that offsets
    // the container's bounding rect away from its final position
    await this.waitForPageStable();
    if (this.destroyGuard.isDestroyed) return;

    const rect = this.readFrameRect();
    try {
      await this.strategy.start({
        facingMode: this.cameraService.activeFacingMode,
        aspectRatio: this.aspectRatio,
        idealResolution: this.idealResolution,
        frameRect: rect,
      });
    } catch (err) {
      this.logging.trackSilentProblem('NativeCameraStartFailed', { error: describeError(err) });
      this.cameraActive = false;
      if (this.destroyGuard.isDestroyed) return;
      this.cd.markForCheck();
      this.cameraError.emit(CameraInitResult.NoCameraWorked);
      return;
    }

    // if the component was destroyed while start() was in flight, stopStream
    // has already run its teardown. Undo the native preview we just started
    // and bail — otherwise setupNativeFrameTracking + onOverlayTap would
    // register observers/listeners that never get cleaned up
    if (this.destroyGuard.isDestroyed) {
      void this.strategy.stop();
      return;
    }

    // seed cameraInfos so the flip button shows (buildOverlayState checks length > 1)
    if (this.cameraService.cameraInfos.length < 2) {
      this.cameraService.cameraInfos = [
        { id: 'environment', name: 'Back', hasFlash: false, isSelfie: false },
        { id: 'user', name: 'Front', hasFlash: false, isSelfie: true },
      ];
    }
    this.activeCameraId = this.cameraService.activeFacingMode;
    this.syncCapabilitiesFromStrategy();
    this.videoReady = true;
    this.setupNativeFrameTracking();

    // subscribe to overlay tap events from the native UI
    this.nativeOverlayUnsub?.();
    this.nativeOverlayUnsub = this.strategy.onOverlayTap((tap, ratio) => {
      if (this.destroyGuard.isDestroyed) return;
      switch (tap) {
        case 'flash': this.toggleFlash(); break;
        case 'flip': void this.openCameraSelector(); break;
        case 'zoomReset': this.resetZoom(); break;
        case 'zoomStop':
          if (ratio != null) this.applyNativeZoomStop(ratio);
          break;
      }
    });

    // push initial overlay state now that capabilities are known
    this.pushOverlayState();

    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();
    this.cameraReady.emit();
  }

  private syncCapabilitiesFromStrategy() {
    this.cameraService.flashSupported = this.strategy.getFlashSupported();
    const limits = this.strategy.getZoomLimits();
    this.cameraService.zoomLimits = limits ? { min: limits.min, max: limits.max } : undefined;
    // native strategy reports its own starting zoom (1x wide lens); web returns 0
    this.cameraService.zoomLevel = this.strategy.initialZoomLevel;
    this.cameraService.flashEnabled = false;
  }

  private readFrameRect(): RectCssPx | undefined {
    const el = this.cameraContainerRef?.nativeElement;
    if (!el) return undefined;
    const r = el.getBoundingClientRect();
    return { x: r.left, y: r.top, width: r.width, height: r.height };
  }

  /** Build the current overlay-control state for the native plugin, derived
   *  from CameraService's capability flags. Called whenever those change
   *  (on start, on flip, on flash toggle, on zoom change). */
  private buildOverlayState(): OverlayState {
    return {
      flashVisible: this.cameraService.flashSupported,
      flashOn: this.cameraService.flashEnabled,
      flipVisible: (this.cameraService.cameraInfos?.length ?? 0) > 1,
      // zoomLimits is an object|undefined — explicit boolean coercion needed
      zoomVisible: !!this.cameraService.zoomLimits,
      zoomLabel: this.zoomLabel,
    };
  }

  private pushOverlayState() {
    if (!this.isNative) return;
    void this.strategy.setOverlayState(this.buildOverlayState());
  }

  /** Wait for any running animations on the closest .cs-page ancestor to
   *  finish. Without this, reading getBoundingClientRect mid-slide returns
   *  a transformed x that no longer matches the final container position. */
  private async waitForPageStable(): Promise<void> {
    const el = this.cameraContainerRef?.nativeElement;
    const page = el?.closest('.cs-page') as HTMLElement | null;
    if (!page) return;
    const anims = (page as unknown as { getAnimations: () => Animation[] }).getAnimations?.();
    if (!anims || anims.length === 0) return;
    await Promise.all(anims.map(a => a.finished.catch(() => undefined)));
  }

  // ── Native frame tracking (resize / scroll / orientation) ──────────

  private setupNativeFrameTracking() {
    const el = this.cameraContainerRef?.nativeElement;
    if (!el) return;
    const push = () => {
      if (this.nativeFrameRaf != null) return;
      this.nativeFrameRaf = requestAnimationFrame(() => {
        this.nativeFrameRaf = null;
        const rect = this.readFrameRect();
        if (rect) void this.strategy.updateFrame(rect);
      });
    };
    this.nativeFrameResizeObserver = new ResizeObserver(push);
    this.nativeFrameResizeObserver.observe(el);
    this.nativeFrameOrientationHandler = push;
    window.addEventListener('orientationchange', this.nativeFrameOrientationHandler);
    window.addEventListener('resize', this.nativeFrameOrientationHandler);

    // transform changes on the .cs-page ancestor (nav slide-in/out) don't
    // trigger ResizeObserver — listen for transitionend explicitly so we
    // re-push the frame once the page settles
    const page = el.closest('.cs-page') as HTMLElement | null;
    if (page) {
      this.nativeFramePageElement = page;
      this.nativeFramePageTransitionHandler = (event) => {
        if (event.propertyName === 'transform') push();
      };
      page.addEventListener('transitionend', this.nativeFramePageTransitionHandler);
    }
  }

  private teardownNativeFrameTracking() {
    this.nativeFrameResizeObserver?.disconnect();
    this.nativeFrameResizeObserver = null;
    if (this.nativeFrameOrientationHandler) {
      window.removeEventListener('orientationchange', this.nativeFrameOrientationHandler);
      window.removeEventListener('resize', this.nativeFrameOrientationHandler);
      this.nativeFrameOrientationHandler = null;
    }
    if (this.nativeFramePageElement && this.nativeFramePageTransitionHandler) {
      this.nativeFramePageElement.removeEventListener('transitionend', this.nativeFramePageTransitionHandler);
      this.nativeFramePageElement = null;
      this.nativeFramePageTransitionHandler = null;
    }
    if (this.nativeFrameRaf != null) {
      cancelAnimationFrame(this.nativeFrameRaf);
      this.nativeFrameRaf = null;
    }
  }

  private async openCameraAsync(cameraId: string | null) {
    await this.cameraService.waitForPermissionAsync();
    const timeoutMs = isPlatformIOS() ? 15_000 : 5_000;

    // the strategy owns the stream (and sets up zoom / flash on it), so the
    // flash, zoom and capture controls act on it without a per-mode branch
    const { stream } = await this.strategy.start({
      deviceId: cameraId,
      facingMode: this.cameraService.activeFacingMode,
      aspectRatio: this.aspectRatio,
      idealResolution: this.idealResolution,
      // if no cameraId (first visit), skip timeout to allow the permission dialog
      openTimeoutMs: cameraId ? timeoutMs : 0,
    });
    // if a stop or another open superseded this one, the strategy already released it
    if (!stream) return;
    this.activeStream = stream;

    // show the video element now that we have a stream
    this.cameraActive = true;
    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();
    await animationFrame();

    let video = this.videoElementRef?.nativeElement;
    if (!video) {
      for (let i = 0; i < 10 && !video; i++) {
        await sleep(100);
        video = this.videoElementRef?.nativeElement;
      }
    }

    if (video && this.activeStream) {
      video.srcObject = this.activeStream;

      // if opened with facingMode (first visit), enumerate cameras and save the resolved ID
      const track = this.activeStream.getVideoTracks()[0];
      if (track && !cameraId) {
        const resolvedId = await this.cameraService.populateCamerasFromTrackAsync(track);
        this.activeCameraId = resolvedId;
      }
      this.videoReady = true;
    }
    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();
    this.cameraReady.emit();
  }

  // ── Camera selector / switch ────────────────────────────────────────

  async openCameraSelector(event?: Event) {
    // iOS native plugin owns the swap animation — call strategy.flip directly
    if (this.isNative) {
      await this.flipNativeAsync();
      return;
    }

    // Android native + barcode mode: Quagga manages its own stream, so the
    // web freeze-frame flip (which depends on the component's `<video>`
    // elements) doesn't apply. Toggle facingMode and reinit the barcode
    // pipeline — the capability-aware route redoes Quagga.init transparently
    if (isCapacitorNative() && this.isBarcodeMode) {
      this.cameraService.activeFacingMode = this.cameraService.activeFacingMode === 'environment' ? 'user' : 'environment';
      await this.stopStream();
      await this.initCameraAsync();
      return;
    }

    // on Capacitor native where isNative is false — reaches here only when
    // the capability-aware fallback routed us onto WebCameraStrategy
    // (never taken today: NativePluginCameraStrategy reports
    // supportsBarcodeDetection = true on iOS and Android) —
    // toggle facingMode and use the web freeze-frame animation
    if (isCapacitorNative()) {
      this.cameraService.activeFacingMode = this.cameraService.activeFacingMode === 'environment' ? 'user' : 'environment';
      await this.switchCameraAsync();
      return;
    }

    // on PWA, show camera list popover — requires the click event's target
    // to anchor the popover; if we were invoked without one (native path
    // shouldn't reach here, but be defensive) bail out
    if (!event) return;
    const isSmallScreen = hasClientSmallScreen();
    const targetEl = (event.currentTarget as HTMLElement);
    const options: CsSelectPopoverOption[] = this.cameraService.cameraInfos.map(cam => ({
      // a camera's name is reported by the device / browser ("FaceTime HD Camera",
      // "back dual wide camera"). It is hardware data, not our copy, so there is nothing
      // to translate and nothing to add to the catalog.
      name: untranslatedString(cam.name),
      value: cam.id,
    }));

    const result = await this.popoverCtrl.present(CsSelectPopover, {
      title: untranslatedString(''),
      options,
      value: this.activeCameraId,
      bottomSheet: isSmallScreen,
    }, {
      targetFromElement: targetEl,
      targetToElement: targetEl,
      animate: true,
      bottomSheet: isSmallScreen,
      verticalAlign: 'top',
      backdropMode: isSmallScreen ? BackdropMode.Visible : BackdropMode.Invisible,
    });

    if (result?.value !== undefined && result.value !== this.activeCameraId) {
      await this.stopStream();
      await this.initCameraAsync(result.value);
      await this.settings.saveAsync();
    }
  }

  // native flip: AVFoundation input swap; animation owned by the plugin
  private async flipNativeAsync() {
    // ignore re-entry while a previous flip is still animating
    if (this.cameraFlipping) return;
    try {
      this.cameraFlipping = true;
      if (!this.destroyGuard.isDestroyed) this.cd.markForCheck();
      const { facingMode } = await this.strategy.flip();
      // if the component was destroyed during the flip animation, skip
      // the state mutations + overlay push — the plugin's own stop path
      // will tear down the native preview
      if (this.destroyGuard.isDestroyed) return;
      this.cameraService.activeFacingMode = facingMode;
      this.activeCameraId = facingMode;
      this.syncCapabilitiesFromStrategy();
      this.pushOverlayState();
    } catch (err) {
      // nothing happens on screen, so the person only sees the flip button not working
      this.logging.trackSilentProblem('NativeCameraFlipFailed', { error: describeError(err) });
    } finally {
      this.cameraFlipping = false;
      if (!this.destroyGuard.isDestroyed) this.cd.markForCheck();
    }
  }

  // switch camera with freeze-frame + 3D flip animation
  private async switchCameraAsync() {
    const currentVideo = this.activeVideo;
    const incomingVideo = this.cameraFlipped
      ? this.videoElementRef?.nativeElement
      : this.videoElementBackRef?.nativeElement;
    const container = this.cameraContainerRef?.nativeElement;
    const flipInner = this.cameraFlipInnerRef?.nativeElement;
    const oldStream = this.activeStream;

    // freeze current frame — iOS kills the old stream when opening a new one
    let freezeImg: HTMLImageElement | null = null;
    if (currentVideo && container && currentVideo.videoWidth > 0) {
      const canvas = document.createElement('canvas');
      canvas.width = currentVideo.videoWidth;
      canvas.height = currentVideo.videoHeight;
      canvas.getContext('2d')!.drawImage(currentVideo, 0, 0);
      freezeImg = document.createElement('img');
      freezeImg.src = canvas.toDataURL('image/jpeg', 0.7);
      freezeImg.className = 'freeze-overlay';
      container.appendChild(freezeImg);
    }

    try {
      // open new camera into the back-face video; the old stream stays live
      // for the animation and is stopped below
      const { stream: newStream } = await this.strategy.start({
        deviceId: null,
        facingMode: this.cameraService.activeFacingMode,
        aspectRatio: this.aspectRatio,
        idealResolution: this.idealResolution,
      });
      // if the camera was stopped while the new one was opening, the strategy released it
      if (!newStream) return;
      this.activeStream = newStream;

      if (incomingVideo) {
        incomingVideo.srcObject = newStream;
      }

      // remove freeze, start flip — new camera is ready on back face
      if (freezeImg) { freezeImg.remove(); freezeImg = null; }
      this.cameraFlipping = true;
      this.cameraFlipped = !this.cameraFlipped;
      if (this.destroyGuard.isDestroyed) {
        oldStream?.getTracks().forEach(t => t.stop());
        newStream?.getTracks().forEach(t => t.stop());
        return;
      }
      this.cd.markForCheck();

      // wait for CSS transition to complete
      await this.waitForFlipTransitionAsync(flipInner);
      // stop old stream regardless of destroy state
      oldStream?.getTracks().forEach(t => t.stop());
      if (this.destroyGuard.isDestroyed) {
        newStream?.getTracks().forEach(t => t.stop());
        return;
      }
      this.cameraFlipping = false;
      this.cd.markForCheck();
    } catch (err) {
      // nothing happens on screen, so the person only sees the switch button not working
      this.logging.trackSilentProblem('CameraSwitchFailed', { error: describeError(err) });
    } finally {
      // safety net — remove freeze if openCameraAsync threw before the flip
      if (freezeImg) freezeImg.remove();
    }
  }

  /** Wait for the flip CSS transition to finish, with a fallback timeout */
  private waitForFlipTransitionAsync(element: HTMLElement | undefined): Promise<void> {
    return new Promise(resolve => {
      if (!element) {
        this.destroyGuard.safeTimeout(resolve, 550);
        return;
      }

      let completed = false;
      const done = () => {
        if (completed) return;
        completed = true;
        element.removeEventListener('transitionend', onTransitionEnd);
        resolve();
      };

      const onTransitionEnd = (event: TransitionEvent) => {
        if (event.target === element && event.propertyName === 'transform') {
          done();
        }
      };

      element.addEventListener('transitionend', onTransitionEnd);

      // fallback in case transitionend doesn't fire
      this.destroyGuard.safeTimeout(done, 550);
    });
  }

  // ── Flash ───────────────────────────────────────────────────────────

  /** Flash button tap, in every mode: the strategy toggles the torch on
   *  whichever camera it holds. */
  toggleFlash() {
    const toggling = this.strategy.toggleFlash();
    // the web strategy flips the flag before it reaches the camera, so the
    // button can show the new state at once
    this.cd.markForCheck();
    void this.finishFlashToggleAsync(toggling);
  }

  /** Records the torch state the strategy reports and pushes it to the native
   *  overlay. Runs on web too, where the value is the one already set. */
  private async finishFlashToggleAsync(toggling: Promise<boolean>) {
    const on = await toggling;
    // if the component was destroyed during the async toggle, skip the
    // state + overlay mutations — matches the post-destroy guard on
    // flipNativeAsync so the native plugin's own stop path is the only
    // side-effect against a destroyed component
    if (this.destroyGuard.isDestroyed) return;
    this.cameraService.flashEnabled = on;
    this.pushOverlayState();
    this.cd.markForCheck();
  }

  // ── Pinch-to-zoom ──────────────────────────────────────────────────

  // computes display label from normalized zoom level (cached for zoneless CD)
  get zoomLabel(): TranslatedString {
    if (!this.videoReady) return this.cachedZoomLabel;
    if (!this.cameraService.zoomLimits) return untranslatedString('1x');
    const actual = this.cameraService.zoomLimits.min + (this.cameraService.zoomLimits.max - this.cameraService.zoomLimits.min) * this.cameraService.zoomLevel;
    const rounded = Math.round(actual * 10) / 10;
    this.cachedZoomLabel = untranslatedString(rounded % 1 === 0 ? `${rounded}x` : `${rounded.toFixed(1)}x`);
    return this.cachedZoomLabel;
  }

  onPinchStart(event: TouchEvent) {
    if (event.touches.length === 2) {
      this.pinchStartDistance = this.getTouchDistance(event);
      this.pinchStartZoom = this.cameraService.zoomLevel;
    }
  }

  onPinchMove(event: TouchEvent) {
    if (event.touches.length !== 2 || !this.cameraService.zoomLimits || !this.pinchStartDistance) return;
    event.preventDefault();

    const distance = this.getTouchDistance(event);
    const scale = distance / this.pinchStartDistance;
    // additive scaling mapped through normalized 0-1 range
    const newZoom = Math.max(0, Math.min(1, this.pinchStartZoom + (scale - 1) * 0.15));
    this.cameraService.zoomLevel = newZoom;
    void this.strategy.setZoom(newZoom);
    this.pushOverlayState();
    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();
  }

  onPinchEnd() {
    this.pinchStartDistance = 0;
  }

  resetZoom() {
    // native resets to the 1x wide lens, not raw minimum (ultra-wide = 0.5x); web resets to 0
    const initial = this.strategy.initialZoomLevel;
    this.cameraService.zoomLevel = initial;
    void this.strategy.setZoom(initial);
    this.pushOverlayState();
    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();
  }

  /** Native-overlay chip tap → jump to an absolute zoom ratio. Converts the chip's
   *  ratio (in the device's `[min, max]` range reported by `getZoomLimits`) to the
   *  normalized 0-1 slider position the strategy layer expects, then pushes the
   *  overlay state so the chip row can repick the active chip + relabel. */
  private applyNativeZoomStop(ratio: number): void {
    const limits = this.cameraService.zoomLimits;
    if (!limits) return;
    const range = limits.max - limits.min;
    if (range <= 0) return;
    const level = Math.max(0, Math.min(1, (ratio - limits.min) / range));
    this.cameraService.zoomLevel = level;
    void this.strategy.setZoom(level);
    this.pushOverlayState();
    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();
  }

  private getTouchDistance(event: TouchEvent): number {
    const [a, b] = [event.touches[0], event.touches[1]];
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }

  // ── Tracker overlay (web barcode mode only) ───────────────────────

  /** Start a fresh gate session. Called from the barcode-init path before
   *  Quagga starts so that any stale state from a previous mount or
   *  strategy switch is cleared. */
  private startTrackerOverlay(): void {
    if (this.isNative) return;
    this.trackerGate.start();
  }

  /** Per-frame drive of the tracker overlay. Runs on every Quagga
   *  `onProcessed` callback in web barcode mode; the directive's
   *  `(processed)` emit is unaffected (page still drives confirmation via
   *  that path). The gate owns selection, gating and emits; this component
   *  only supplies the transform into the rendered container's space. */
  private handleProcessedForOverlay(result: QuaggaProcessResult): void {
    this.trackerGate.onFrame(result, pick => this.pickToDisplayRect(pick));
  }

  /** Transform a decoder-space pick into the camera container's video
   *  space, or null when the decoder or container dims aren't available. */
  private pickToDisplayRect(pick: TrackerPick): TrackerRect | null {
    const sourceDims = this.strategy.getDecoderSourceDims();
    if (!sourceDims) return null;
    const container = this.cameraContainerRef?.nativeElement;
    if (!container) return null;
    return transformDecoderRectToVideoSpace(
      pick.rect, sourceDims.width, sourceDims.height, container.clientWidth, container.clientHeight,
    );
  }

  // ── Stream lifecycle ───────────────────────────────────────────────

  private async stopStream(): Promise<void> {
    this.videoReady = false;
    this.cameraActive = false;
    // Tear down tracker-overlay state first so any late selector / gate
    // callbacks that arrive during the (async) Quagga.stop drop silently.
    if (this.isBarcodeMode && !this.isNative) {
      this.trackerGate.stop();
    }
    if (this.isBarcodeMode) {
      // Barcode-mode teardown on native: the only thing the user cares
      // about visually is the preview layer disappearing, which is done
      // inside `strategy.stop()` (plugin's main-queue visual teardown).
      // `stopBarcodeDetection` only clears the metadata-output delegate —
      // no visual effect. Fire-and-forget the barcode teardown so our
      // await cost is just the visual teardown, not the sum of both.
      const container = this.cameraContainerRef?.nativeElement;
      this.strategy.stopBarcodeDetection(container).catch(err => {
        // both native plugins always resolve this, so in practice a rejection comes from the web decoder
        this.logging.trackSilentProblem('BarcodeDetectionStopFailed', { isNative: this.isNative, error: describeError(err) });
      });
      if (this.isNative) {
        this.nativeOverlayUnsub?.();
        this.nativeOverlayUnsub = null;
        this.teardownNativeFrameTracking();
        try { await this.strategy.stop(); }
        catch (err) {
          // a failed native stop can leave the preview layer on top of the app after the page has gone
          this.logging.trackSilentProblem('NativeCameraStopFailed', { barcodeMode: true, error: describeError(err) });
        }
      }
      return;
    }
    if (this.isNative) {
      this.nativeOverlayUnsub?.();
      this.nativeOverlayUnsub = null;
      this.teardownNativeFrameTracking();
      try { await this.strategy.stop(); }
      catch (err) {
        // a failed native stop can leave the preview layer on top of the app after the page has gone
        this.logging.trackSilentProblem('NativeCameraStopFailed', { barcodeMode: false, error: describeError(err) });
      }
      return;
    }
    // web photo: the strategy owns the stream, and stopping it also releases
    // any camera that is still opening
    await this.strategy.stop();
    this.activeStream = null;
  }
}
